import {Injectable, Type} from '@angular/core';
import {defer, from, Observable, of} from 'rxjs';
import {catchError, map, reduce, shareReplay} from 'rxjs/operators';
import {Resource} from '../../../api/resource';
import {Uri} from '../../../api/uri';
import {FilterAndData} from '../../../api/filter-list';
import {InterfaceProviderService} from '../../../basic-entity-back/services/interface-provider.service';
import {InternalPropertyMap} from '../../../basic-entity-back/basic-entity-interface/mapping-internal';
import {ManagerCachingStrategy} from '../../../basic-entity-back/basic-entity-interface/mapping-external';
import {GeneralSearchFilter} from '../../../basic-entity-back/filters/general-search-filter';
import {TypeStr} from '../../../basic-entity-back/property-type/type-str';
import {OpcionFiltro} from './panel-filtros';

/** Cuántas sugerencias pide el buscador */
export const MAX_SUGERENCIAS = 10;

/**
 * Las opciones de las secciones de relación del panel de filtros: las casillas de un catálogo, las sugerencias del
 * buscador y la etiqueta de un valor que llega por la URL. La etiqueta es el nombre del modelo (`modelNameProperty`
 * o `modelNamePattern`) y el valor, el IRI.
 */
@Injectable({
    providedIn: 'root'
})
export class OpcionesFiltroService {
    /**
     * Las opciones de cada catálogo, para toda la sesión: cambian poco y se piden en cada tabla que filtra por él.
     * Un elemento nuevo del catálogo no aparece hasta recargar la página.
     */
    private readonly _cache = new Map<Type<Resource>, Observable<OpcionFiltro[]>>();

    constructor(private _interfaceProvider: InterfaceProviderService) {
    }

    /**
     * Todas las opciones de un catálogo, ordenadas alfabéticamente (es). Pide todas las páginas y emite una sola vez,
     * al final (no se usa `manager.all`: con `NoCache` está vacío). Si falla, emite una lista vacía y no la guarda,
     * para que otra tabla lo vuelva a intentar.
     */
    public opciones(modelo: Type<Resource>): Observable<OpcionFiltro[]> {
        let opciones = this._cache.get(modelo);
        if (!opciones) {
            opciones = this._interfaceProvider.managerForModel(modelo).loader.findAndFollow().pipe(
                reduce((todos: Resource[], pagina) => todos.concat(pagina.member), []),
                map(todos => todos
                    .map(item => this._opcion(modelo, item))
                    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es'))),
                catchError(() => {
                    this._cache.delete(modelo);
                    return of([] as OpcionFiltro[]);
                }),
                shareReplay(1)
            );
            this._cache.set(modelo, opciones);
        }
        return opciones;
    }

    /** Las sugerencias del buscador: la primera página (MAX_SUGERENCIAS) de la búsqueda general, en el orden de la API */
    public sugerencias(modelo: Type<Resource>, texto: string): Observable<OpcionFiltro[]> {
        return this._interfaceProvider.managerForModel(modelo).loader
            .find([new FilterAndData(GeneralSearchFilter, texto)], null, null, 0, MAX_SUGERENCIAS)
            .pipe(map(pagina => pagina.member.map(item => this._opcion(modelo, item))));
    }

    /** El nombre de un elemento a partir de su IRI (las etiquetas del buscador que llegan por la URL) */
    public etiqueta(modelo: Type<Resource>, iri: string): Observable<string> {
        // defer: manager.get lanza en el acto con un IRI que no es de ese modelo; así llega como error del observable
        return defer(() => from(this._interfaceProvider.managerForModel(modelo).get(iri)))
            .pipe(map(item => this._interfaceProvider.interfaceForModel(modelo).getName(item) || iri));
    }

    /**
     * Si las opciones de un modelo van en buscador y no en casillas: los modelos que no se cachean (`NoCache`, p. ej.
     * Cliente) son los que tienen demasiados elementos para cargarlos todos.
     */
    public usaBuscador(modelo: Type<Resource>): boolean {
        return this._interfaceProvider.interfaceForModel(modelo).managerCaching === ManagerCachingStrategy.NoCache;
    }

    /**
     * Los modelos de los que salen las opciones de una relación: el de un NestedModel; en una Uri, los que acepta
     * (su clase o, si es una clase padre, sus hijas registradas). Otra propiedad no tiene.
     */
    public modelosDe(propiedad: InternalPropertyMap): Type<Resource>[] {
        const tipo = propiedad.type.toString();
        if (tipo === TypeStr.NestedModel) {
            return [propiedad.type.asNestedModel().modelType];
        }
        if (tipo === TypeStr.Uri) {
            return this._interfaceProvider.allAcceptedInterfaces(propiedad.type.asUri().modelTypeOrParent)
                .map(intrf => intrf.serialiser.model);
        }
        return [];
    }

    /**
     * Un IRI de una casilla convertido a lo que espera el filtro, igual que al leerlo de la URL
     * (RouteFilterTranslator): en un NestedModel, una Uri con el patrón de su interfaz; en una Uri, su caster.
     */
    public aUri(propiedad: InternalPropertyMap, iri: string): Uri {
        if (propiedad.type.toString() === TypeStr.NestedModel) {
            const intrf = this._interfaceProvider.interfaceForModel(propiedad.type.asNestedModel().modelType);
            return Uri.fromUriStr(iri, intrf.uriPattern, intrf.serialiser.model);
        }
        return propiedad.caster.toModel(iri);
    }

    private _opcion(modelo: Type<Resource>, item: Resource): OpcionFiltro {
        const iri = item.iri.toString();
        return {etiqueta: this._interfaceProvider.interfaceForModel(modelo).getName(item) || iri, valor: iri};
    }
}
